import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockIcon from '@mui/icons-material/Lock';
import { useRoles, useDeleteRole, useCloneRole } from '../../hooks/useRoles';
import { useSnackbar } from '../../components/SnackbarProvider';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import { Role } from '../../types';

// Helper to get hierarchy level label
const getHierarchyLabel = (level: number): string => {
  switch (level) {
    case 1: return 'Level 1 (Owner)';
    case 2: return 'Level 2 (Manager)';
    case 3: return 'Level 3 (Staff)';
    default: return `Level ${level}`;
  }
};

// Helper to get hierarchy chip color
const getHierarchyColor = (level: number): 'error' | 'warning' | 'info' => {
  switch (level) {
    case 1: return 'error';
    case 2: return 'warning';
    case 3: return 'info';
    default: return 'info';
  }
};

export const RoleList = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const { canModifyRole } = usePermissionsContext();

  const { data: roles, isLoading } = useRoles();
  const deleteRoleMutation = useDeleteRole();
  const cloneRoleMutation = useCloneRole();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);

  const handleDeleteClick = (role: Role) => {
    if (role.isSystemDefault) {
      showMessage('System default roles cannot be deleted. You can clone it instead.', 'warning');
      return;
    }
    if (!canModifyRole(role.hierarchyLevel)) {
      showMessage('You cannot delete a role at the same or higher level than your own.', 'warning');
      return;
    }
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (roleToDelete) {
      deleteRoleMutation.mutate(roleToDelete.id, {
        onSuccess: () => {
          showMessage('Role deleted successfully', 'success');
          setDeleteDialogOpen(false);
          setRoleToDelete(null);
        },
        onError: (error: any) => {
          showMessage(
            error.response?.data?.message || 'Failed to delete role',
            'error'
          );
        },
      });
    }
  };

  const handleCloneClick = (role: Role) => {
    cloneRoleMutation.mutate(role.id, {
      onSuccess: (newRole) => {
        showMessage(`Role "${role.name}" cloned successfully`, 'success');
        navigate(`/app/roles/edit/${newRole.id}`);
      },
      onError: (error: any) => {
        showMessage(
          error.response?.data?.message || 'Failed to clone role',
          'error'
        );
      },
    });
  };

  const getPermissionCount = (role: Role) => {
    return role.rolePermissions?.length || 0;
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Role Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          {params.row.isSystemDefault && (
            <Tooltip title="System Default Role">
              <LockIcon fontSize="small" color="action" />
            </Tooltip>
          )}
          <Typography variant="body2">{params.value}</Typography>
          {params.row.isSystemDefault && (
            <Chip
              label="Default"
              size="small"
              color="default"
              variant="outlined"
            />
          )}
        </Box>
      ),
    },
    {
      field: 'hierarchyLevel',
      headerName: 'Hierarchy',
      width: 150,
      renderCell: (params) => (
        <Tooltip title="Lower level = more privilege. Can only modify roles below your level.">
          <Chip
            label={getHierarchyLabel(params.value)}
            size="small"
            color={getHierarchyColor(params.value)}
            variant="outlined"
          />
        </Tooltip>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'permissions',
      headerName: 'Permissions',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={`${getPermissionCount(params.row)} permissions`}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const canEdit = canModifyRole(params.row.hierarchyLevel);
        const canDelete = canEdit && !params.row.isSystemDefault;

        return (
          <Box>
            <Tooltip title={canEdit ? 'Edit' : 'Cannot edit role at same or higher level'}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => navigate(`/app/roles/edit/${params.row.id}`)}
                  disabled={!canEdit}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Clone">
              <IconButton
                size="small"
                onClick={() => handleCloneClick(params.row)}
                disabled={cloneRoleMutation.isPending}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={
              params.row.isSystemDefault
                ? 'Cannot delete default roles'
                : !canEdit
                  ? 'Cannot delete role at same or higher level'
                  : 'Delete'
            }>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(params.row)}
                  disabled={!canDelete}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Roles & Permissions</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Create custom roles with granular permissions for your staff
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/app/roles/create')}
        >
          Create Role
        </Button>
      </Box>

      <Card>
        <CardContent>
          <DataGrid
            rows={roles || []}
            columns={columns}
            loading={isLoading}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            sx={{
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the role "{roleToDelete?.name}"?
            Users assigned to this role will need to be reassigned to another role.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteRoleMutation.isPending}
          >
            {deleteRoleMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleList;
